import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authAPI, apiUtils } from '../services/api';
import api from '../services/api';

// Auth context
const AuthContext = createContext();

// Auth actions
const AUTH_ACTIONS = {
    SET_LOADING: 'SET_LOADING',
    LOGIN_SUCCESS: 'LOGIN_SUCCESS',
    LOGOUT: 'LOGOUT',
    SET_ERROR: 'SET_ERROR',
    CLEAR_ERROR: 'CLEAR_ERROR',
    UPDATE_USER: 'UPDATE_USER',
    SUBSCRIPTION_LOADED: 'SUBSCRIPTION_LOADED',
};

// Initial state
const initialState = {
    user: null,
    isAuthenticated: false,
    loading: true,
    error: null,
    hasActiveSubscription: false,
    subscriptionPlan: null,
};

// Auth reducer
const authReducer = (state, action) => {
    switch (action.type) {
        case AUTH_ACTIONS.SET_LOADING:
            return {
                ...state,
                loading: action.payload,
            };

        case AUTH_ACTIONS.LOGIN_SUCCESS:
            return {
                ...state,
                user: action.payload,
                isAuthenticated: true,
                loading: false,
                error: null,
            };

        case AUTH_ACTIONS.LOGOUT:
            return {
                ...state,
                user: null,
                isAuthenticated: false,
                loading: false,
                error: null,
                hasActiveSubscription: false,
                subscriptionPlan: null,
            };

        case AUTH_ACTIONS.SUBSCRIPTION_LOADED:
            return {
                ...state,
                hasActiveSubscription: action.payload.hasActive,
                subscriptionPlan: action.payload.plan,
            };

        case AUTH_ACTIONS.SET_ERROR:
            return {
                ...state,
                error: action.payload,
                loading: false,
            };

        case AUTH_ACTIONS.CLEAR_ERROR:
            return {
                ...state,
                error: null,
            };

        case AUTH_ACTIONS.UPDATE_USER:
            return {
                ...state,
                user: action.payload,
            };

        default:
            return state;
    }
};

// Auth provider component
export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    const loadSubscriptionStatus = async () => {
        try {
            const response = await api.get('/users/my/subscriptions');
            if (response.data.success) {
                const subs = response.data.data || [];
                const activeSub = subs.find(s =>
                    s.status === 'ACTIVE' ||
                    s.status === 'PENDING' ||
                    (s.status === 'CANCELED' && s.current_period_end && new Date(s.current_period_end) > new Date())
                );
                dispatch({
                    type: AUTH_ACTIONS.SUBSCRIPTION_LOADED,
                    payload: { hasActive: !!activeSub, plan: activeSub || null }
                });
            }
        } catch {
            // Subscription check failed — treat as no subscription
        }
    };

    // Check for stored authentication on mount
    useEffect(() => {
        const checkAuth = async () => {
            try {
                dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

                // Check if token exists
                if (!apiUtils.isAuthenticated()) {
                    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
                    return;
                }

                // Get stored user data
                const storedUser = apiUtils.getStoredUser();
                if (storedUser) {
                    dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: storedUser });
                }

                // Verify token with server
                try {
                    const response = await authAPI.getCurrentUser();
                    dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: response.user });
                    loadSubscriptionStatus();
                } catch (error) {
                    // Token is invalid, clear auth
                    apiUtils.clearAuth();
                    dispatch({ type: AUTH_ACTIONS.LOGOUT });
                }
            } catch (error) {
                console.error('Auth check error:', error);
                dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: 'Authentication check failed' });
            }
        };

        checkAuth();
    }, []);

    // Register function
    const register = async (userData) => {
        try {
            dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

            const response = await authAPI.register(userData);
            dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: response.user });

            return { success: true, data: response };
        } catch (error) {
            const errorMessage = apiUtils.formatErrorMessage(error);
            dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
            return { success: false, error: errorMessage };
        }
    };

    // Login function
    const login = async (credentials) => {
        try {
            dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

            const response = await authAPI.login(credentials);
            dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: response.user });
            loadSubscriptionStatus();

            return { success: true, data: response };
        } catch (error) {
            const errorMessage = apiUtils.formatErrorMessage(error);
            dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
            return { success: false, error: errorMessage };
        }
    };

    // Logout function
    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
    };

    // Update user data
    const updateUser = (userData) => {
        dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: userData });
    };

    // Clear error
    const clearError = () => {
        dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
    };

    // Check if user is artist
    const isArtist = () => {
        return state.user?.profile?.isArtist === true;
    };

    // Check if user is verified
    const isVerified = () => {
        return state.user?.isVerified === true;
    };

    // Check if artist is verified
    const isArtistVerified = () => {
        return isArtist() && state.user?.profile?.artistVerified === true;
    };

    // Check if user is admin
    const isAdmin = () => {
        return state.user?.is_admin === true;
    };

    const value = {
        // State
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        loading: state.loading,
        error: state.error,
        hasActiveSubscription: state.hasActiveSubscription,
        subscriptionPlan: state.subscriptionPlan,

        // Actions
        register,
        login,
        logout,
        updateUser,
        clearError,
        loadSubscriptionStatus,

        // Utils
        isArtist,
        isVerified,
        isArtistVerified,
        isAdmin,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// HOC for protected routes
export const withAuth = (Component) => {
    return function AuthenticatedComponent(props) {
        const { isAuthenticated, loading } = useAuth();

        if (loading) {
            return (
                <div className="min-h-screen flex items-center justify-center">
                    <div className="loading-spinner w-8 h-8"></div>
                </div>
            );
        }

        if (!isAuthenticated) {
            // Redirect to login or show login form
            return null; // This should be handled by routing
        }

        return <Component {...props} />;
    };
};

// HOC for artist-only routes
export const withArtistAuth = (Component) => {
    return function ArtistAuthenticatedComponent(props) {
        const { isAuthenticated, isArtist, loading } = useAuth();

        if (loading) {
            return (
                <div className="min-h-screen flex items-center justify-center">
                    <div className="loading-spinner w-8 h-8"></div>
                </div>
            );
        }

        if (!isAuthenticated || !isArtist()) {
            // Redirect or show access denied
            return (
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-300 mb-4">
                            Artist Access Required
                        </h2>
                        <p className="text-gray-500">
                            This page is only accessible to users with artist status.
                        </p>
                    </div>
                </div>
            );
        }

        return <Component {...props} />;
    };
};

export default AuthContext;